import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { format, isPast, differenceInDays } from 'date-fns';
import { Clock, AlertTriangle, ClipboardCheck, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QualityPendingFinalList: React.FC = () => {
  const navigate = useNavigate();
  
  const { data: procurements, isLoading } = useQuery({
    queryKey: ['quality-pending-final-procurements'],
    queryFn: async () => {
      // First, get procurement IDs that need final grading (server-side filter)
      const { data: qualityRows, error: qError } = await supabase
        .from('procurement_quality')
        .select('procurement_id')
        .eq('grading_type', 'preliminary')
        .eq('is_final_grading_complete', false);

      if (qError) throw qError;
      if (!qualityRows || qualityRows.length === 0) return [];

      const procurementIds = qualityRows.map((q: any) => q.procurement_id);

      const { data, error } = await supabase
        .from('procurements')
        .select(`
          *,
          farms:farm_id(farmer_name, region),
          procurement_quality(
            *,
            grading_type,
            preliminary_damage_kg,
            preliminary_compensation_kg,
            preliminary_damage_percentage,
            preliminary_graded_at,
            final_grading_expected_date,
            is_final_grading_complete
          ),
          items:procurement_items(id, product_name, quantity)
        `)
        .in('id', procurementIds)
        .not('status', 'eq', 'rejected')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getProductNames = (procurement: any) => {
    const items = procurement.items || [];
    if (items.length === 0) {
      return procurement.product_name || 'Unknown Product';
    }
    if (items.length <= 3) {
      return items.map((i: any) => i.product_name).join(', ');
    }
    return `${items.slice(0, 2).map((i: any) => i.product_name).join(', ')} + ${items.length - 2} more`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (!procurements || procurements.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No procurements awaiting final grading</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {procurements.map((procurement) => {
        const quality = (procurement as any).procurement_quality;
        const items = (procurement as any).items || [];
        const expectedDate = quality?.final_grading_expected_date 
          ? new Date(quality.final_grading_expected_date) 
          : null;
        const isOverdue = expectedDate ? isPast(expectedDate) : false;
        const daysUntil = expectedDate ? differenceInDays(expectedDate, new Date()) : null;

        return (
          <Card key={procurement.id} className={isOverdue ? 'border-destructive' : 'border-amber-200'}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">
                    {getProductNames(procurement)}
                  </CardTitle>
                  {items.length > 1 && (
                    <Badge variant="secondary" className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      {items.length} items
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                    <Clock className="h-3 w-3 mr-1" />
                    Preliminary
                  </Badge>
                  {isOverdue ? (
                    <Badge variant="destructive">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Overdue
                    </Badge>
                  ) : expectedDate && (
                    <Badge variant="outline">
                      {daysUntil === 0 ? 'Due Today' : `${daysUntil} days left`}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{procurement.total_quantity || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Damage</p>
                  <p className="font-medium text-amber-600">{quality?.preliminary_damage_kg || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Compensation</p>
                  <p className="font-medium text-amber-600">{quality?.preliminary_compensation_kg || 0} kg</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Preliminary Graded</p>
                  <p className="font-medium">
                    {quality?.preliminary_graded_at 
                      ? format(new Date(quality.preliminary_graded_at), 'MMM dd, yyyy')
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expected Final</p>
                  <p className={`font-medium ${isOverdue ? 'text-destructive' : ''}`}>
                    {expectedDate 
                      ? format(expectedDate, 'MMM dd, yyyy')
                      : 'Not set'}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={() => navigate(`/quality/final-grading/${procurement.id}`)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Complete Final Grading
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default QualityPendingFinalList;
